import styles from "./index.module.scss";
import { useTranslations } from "next-intl";
import React, { useEffect, useState } from "react";
import {
  PaymentSystem,
  PaymentSystemFormDataType,
  UsePaymentErrorItemType,
} from "@/domains/profile/types";
import Image from "next/image";

import { SubmitHandler, useForm } from "react-hook-form";
import { usePaymentForm } from "@/domains/profile/hooks/use-payment-form";
import { IMaskInput } from "react-imask";
import { useStore } from "@/hooks/use-store";
import getSymbolFromCurrency from "currency-symbol-map";

export default function DepositTab({
  paymentSystems,
  type,
}: {
  paymentSystems: PaymentSystem[];
  type: string;
}) {
  const tForm = useTranslations("DepositModalForm");
  const tValidation = useTranslations("Validation");

  const [selectedMethod, setSelectedMethod] = useState<PaymentSystem | null>(null);
  const [errorsPayment, setErrorsPayment] = useState<Record<
    string,
    UsePaymentErrorItemType
  > | null>(null);

  const { onMutatePayment, errors: paymentErrors, loading } = usePaymentForm();
  const { modal } = useStore();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PaymentSystemFormDataType>({
    mode: "all",
  });

  const isCrypto = selectedMethod?.name?.startsWith("TDX_");

  const handleChange = (item: PaymentSystem | null) => {
    setSelectedMethod(item);
    setErrorsPayment(null);
    reset({
      receiverWallet: "",
    });
  };

  const onSubmit: SubmitHandler<PaymentSystemFormDataType> = async (data) => {
    if (!selectedMethod) return;

    data.type = type;
    data.method = selectedMethod.id;

    if (data.amount !== undefined && data.amount <= 0) {
      return;
    }

    const result = await onMutatePayment(data);

    if (result.success) {
      reset();

      if (result.response?.url) {
        window.location.href = result.response.url;
      }

      modal.closeModal();
    }
  };

  useEffect(() => {
    setErrorsPayment(null);
    setSelectedMethod(null);
    reset();
  }, [type]);

  useEffect(() => {
    if (!paymentErrors) {
      setErrorsPayment(null);
      return;
    }

    setErrorsPayment((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(paymentErrors)) {
        return prev;
      }
      return paymentErrors;
    });
  }, [paymentErrors]);

  return (
    <>
      <div className={styles.depositGrid}>
        {paymentSystems.map((item) => (
          <button
            key={item.id}
            className={`${styles.depositMethodCard} ${
              item.id === selectedMethod?.id ? styles.active : ""
            }`}
            onClick={() => handleChange(item)}
          >
            <div className={styles.depositMethodLogo}>
              <Image
                width={100}
                height={50}
                src={`/images/payments/${item.name}.svg`}
                alt={item.name}
                className="h-full object-contain"
                quality={100}
              />
            </div>
            <div className={styles.depositRange}>
              {item.min} - {item.max} {getSymbolFromCurrency(item.currency || "")}
            </div>
          </button>
        ))}
      </div>

      {selectedMethod && (
        <div className={styles.depositFormContainer}>
          {selectedMethod.type === "in" ? (
            <div className={styles.depositFormWarning}>{tForm("warning_info")}</div>
          ) : isCrypto && selectedMethod.type === "out" ? (
            <div className={styles.depositFormWarning}>
              <div>
                {tForm("warning_crypto_info", { network: selectedMethod.name.split("_").at(-1) })}
              </div>
              <div>{tForm("warning_crypto_info_commission")}</div>
            </div>
          ) : selectedMethod.type === "out" ? (
            <div className={styles.depositFormWarning}>{tForm("warning_card_info")}</div>
          ) : null}

          <form
            className={styles.depositForm}
            onSubmit={(e) => {
              handleSubmit(onSubmit)(e);
            }}
          >
            <div className={styles.depositFormRange}>
              <span>
                {tForm("network")}: {selectedMethod.name.split("_").at(-1)}
                {" | "}
              </span>
              <span>
                {tForm("min")}: {selectedMethod.min}{" "}
                {getSymbolFromCurrency(selectedMethod.currency || "")}
              </span>
              <span> / </span>
              <span>
                {tForm("max")}: {selectedMethod.max}{" "}
                {getSymbolFromCurrency(selectedMethod.currency || "")}
              </span>
            </div>

            {type === "out" && (
              <div className={styles.depositFormField}>
                <label className={styles.label}>
                  {isCrypto ? (
                    <>
                      <input
                        {...register("receiverWallet", { required: tValidation("required") })}
                        className={`${styles.input} ${errorsPayment?.receiverWallet ? styles.active : ""}`}
                        placeholder=""
                        onChange={(e) =>
                          setValue("receiverWallet", e.target.value, { shouldValidate: true })
                        }
                      />

                      <span className={styles.label_value}>{tForm("address")}</span>
                    </>
                  ) : (
                    <>
                      <IMaskInput
                        {...register("receiverWallet", {
                          required: tValidation("required"),
                        })}
                        mask="0000 0000 0000 0000"
                        unmask
                        className={`${styles.input} ${errorsPayment?.receiverWallet ? styles.active : ""}`}
                        placeholder=""
                        onAccept={(val) => {
                          const current = String(watch("receiverWallet") ?? "");
                          if (current === String(val)) return;
                          setValue("receiverWallet", String(val), { shouldValidate: true });
                        }}
                      />

                      <span className={styles.label_value}>{tForm("card")}</span>
                    </>
                  )}
                </label>

                {errorsPayment?.receiverWallet && (
                  <p className={styles.error}>
                    {tForm(`error_${errorsPayment.receiverWallet.error}`, {
                      min: selectedMethod.min,
                      max: selectedMethod.max,
                    })}
                  </p>
                )}

                {errors?.receiverWallet && (
                  <p className={styles.error}>{errors.receiverWallet.message}</p>
                )}
              </div>
            )}

            <div className={styles.depositFormField}>
              <label className={styles.label}>
                <IMaskInput
                  {...register("amount", {
                    required: tValidation("required"),
                  })}
                  mask={Number}
                  scale={0}
                  radix=","
                  mapToRadix={[".", ","]}
                  thousandsSeparator=" "
                  min={undefined}
                  max={undefined}
                  unmask
                  className={`${styles.input} ${errorsPayment?.amount ? styles.active : ""}`}
                  placeholder=""
                  onAccept={(val) => {
                    const next = Number(val);
                    const current = Number(watch("amount") ?? 0);
                    if (current === next) return;
                    setValue("amount", next, { shouldValidate: true });
                  }}
                />

                <span className={styles.label_value}>{tForm("amount")}</span>
              </label>

              {errorsPayment?.amount && (
                <p className={styles.error}>
                  {tForm(`error_${errorsPayment.amount.error}`, {
                    min: selectedMethod.min,
                    max: selectedMethod.max,
                    ...errorsPayment.amount.params,
                  })}
                </p>
              )}

              {errors?.amount && <p className={styles.error}>{errors.amount.message}</p>}
            </div>

            <button className={styles.depositFormSubmit} type="submit" disabled={loading}>
              {loading ? tForm("submit_wait") : tForm("submit")}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
